import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { useState, useEffect } from "react";
import { TYPO } from "styles/Typo";
import { KOREA } from "utils/constant/ITME_INFO";
import InterestStar from "../../components/InterestStar";
import DividerButton from "components/common/divider/button";
import { useTrendingIndustry } from "utils/clientAPIs/industry";
import Img from "components/common/List/img";
import Text from "components/common/List/Text";
import { TrendIndustrySkeleton } from "components/skeleton/main";
import { getMainTime } from "utils/api/time";
import StockDetailLink from "components/common/StockDetailLink";
import { TrackedList, TrackedListItem } from "lib/analytics/react/list";
import { TrackedListMoreLink } from "lib/analytics/react/link";
import { trackPageAction } from "lib/analytics/core/actions";
import { ERROR_LOGO_URL, getPublicAssetUrl } from "utils/objectStorage";

function HighMomentum({}: {}) {
  const router = useRouter();
  const { t } = useTranslation("main");

  const isKorean = getMainTime();

  const [data, isValid] = useTrendingIndustry({
    limit: 5,
  });
  const [activeIndustry, setActiveIndustry] = useState<number>(
    data?.[0]?.TU_CD
  ); // TU_CD로 변경

  useEffect(() => {
    setActiveIndustry(data?.[0]?.TU_CD);
  }, [data]);

  const industryNames =
    router.locale === "ko"
      ? data?.map((item: any) => item?.TU_NM)
      : data?.map((item: any) => item?.TU_ENG_NM);

  const currentAssets =
    data?.find((item: any) => item?.TU_CD === activeIndustry) || [];

  if (isValid)
    return (
      <div className="bg-white">
        <TrendIndustrySkeleton />
      </div>
    );

  if (data?.length === 0 || !data) return null;
  return (
    <div className="bg-white">
      <div className="flex flex-col w-full gap-2">
        <div className="px-6 pt-6">
          <span className={`${TYPO.TYPO4_SB} text-gray80`}>
            {t("trendingIndustries")}
          </span>
        </div>
        <div className={"flex flex-col gap-6 relative pt-2 bg-white pb-4 "}>
          <div className={`w-full`}>
            <div className="flex gap-2 overflow-auto scroll px-5 h-[32px]">
              {data?.length > 0 && !isValid
                ? data?.slice(0, 9).map((item: any, index: number) => {
                    return (
                      <div
                        key={index}
                        data-gtm-id={`explore-industry-button`}
                        onClick={() => {
                          trackPageAction("filter_sector");
                          setActiveIndustry(item?.TU_CD);
                        }}
                        className={`flex items-center justify-center px-3 py-[6px] ${
                          TYPO.TYPO7_1_MD
                        } ${
                          item?.TU_CD === activeIndustry
                            ? "text-gray80 bg-gray10 rounded-[8px]"
                            : "text-gray50"
                        } text-gray50 shrink-0 cursor-pointer`}
                      >
                        {industryNames[index]}
                      </div>
                    );
                  })
                : ""}
            </div>
          </div>
        </div>
        <TrackedList
          listId="explore_hot_industries_list"
          totalCount={currentAssets?.assets?.length}
        >
          <ul>
            {currentAssets?.assets?.map((asset: any, index: number) => (
              <li
                key={index}
                className={` w-full flex items-center  px-6 py-2  gap-3 bg-white  `}
              >
                <StockDetailLink
                  ticker={asset.ITEM_CD_DL}
                  dataGtmId={`explore-industry-list`}
                  className=" w-full"
                >
                  <TrackedListItem
                    itemData={{
                      kind: "stock",
                      rank: index + 1,
                      ticker: asset.ITEM_CD_DL,
                      market: asset?.LOC === KOREA ? "kr" : "us",
                    }}
                    className="flex gap-3 grow w-full"
                  >
                    <Img
                      src={
                        getPublicAssetUrl(`logos/${asset.ITEM_CD_DL}.png`) ||
                        ERROR_LOGO_URL
                      }
                      HR_ITEM_NM={""}
                    />

                    <div
                      className={`justify-start grow  overflow-hidden whitespace-pre-wrap flex-col  items-start inline-flex `}
                    >
                      <Text
                        item={
                          router.locale === "ko"
                            ? asset.ITEM_KR_NM && asset.ITEM_KR_NM
                            : asset.ITEM_ENG_NM && asset.ITEM_ENG_NM
                        }
                        price={
                          router.locale === "ko"
                            ? asset.ADJ_CLOSE_KRW &&
                              asset.ADJ_CLOSE_KRW.toLocaleString("en-us", {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              }) + (asset.CAT !== "Index" ? "원" : "")
                            : asset.ADJ_CLOSE &&
                              asset.ADJ_CLOSE.toLocaleString("en-us", {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              }) +
                                (asset.CAT !== "Index"
                                  ? asset?.LOC === KOREA
                                    ? "₩"
                                    : "$"
                                  : "")
                        }
                        percent={asset.ADJ_CLOSE_CHG.toLocaleString("en-us", {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 2,
                        })}
                      />
                    </div>
                  </TrackedListItem>
                </StockDetailLink>

                <div className="flex items-center shrink-0 justify-end">
                  <div>
                    {
                      <InterestStar
                        asset={asset}
                        size={30}
                        triggerSectionId="explore_hot_industries_list"
                      />
                    }
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </TrackedList>
      </div>
      <TrackedListMoreLink
        href={`/industryranking?loc=${isKorean ? "korea" : "us"}`}
        listId="explore_hot_industries_list"
        data-gtm-id={`explore-industry-list-more`}
      >
        <DividerButton
          text={t("viewMore")}
          onClick={() => {
            router.push(`/industryranking?loc=${isKorean ? "korea" : "us"}`);
          }}
          data-gtm-id={`explore-industry-list-more`}
        />
      </TrackedListMoreLink>
    </div>
  );
}

export default HighMomentum;
